import type { Asset, AssetGroup } from '../models/asset';
import type { IAsset, IAssetGroup } from './memoryAssetService';

interface IndexedAsset extends IAsset {
    groupIds: string[];
    normalizedSearchText: string;
}

export interface AssetTreeIndex {
    assets: IndexedAsset[];
    assetsByGroupId: Map<string, IAsset[]>;
    allAssetIds: Set<string>;
}

export interface FilteredAssetTree {
    groups: IAssetGroup[];
    assetsByGroupId: Map<string, IAsset[]>;
    assetIds: Set<string>;
}

export const buildAssetTreeIndex = (allAssets: Asset[]): AssetTreeIndex => {
    const assetsByGroupId = new Map<string, IAsset[]>();
    const allAssetIds = new Set<string>();
    const assets = allAssets.map(asset => {
        const indexedAsset: IndexedAsset = {
            id: asset.id,
            name: asset.name,
            vehicleType: asset.type ?? 'car',
            groupIds: asset.groupIds ?? [],
            normalizedSearchText: `${asset.name ?? ''} ${asset.chassis ?? ''}`.toLowerCase(),
        };

        allAssetIds.add(asset.id);

        for (const groupId of indexedAsset.groupIds) {
            const groupAssets = assetsByGroupId.get(groupId);
            if (groupAssets) {
                groupAssets.push(indexedAsset);
            } else {
                assetsByGroupId.set(groupId, [indexedAsset]);
            }
        }

        return indexedAsset;
    });

    return { assets, assetsByGroupId, allAssetIds };
};

const filterTreeByAssets = (
    assetTreeIndex: AssetTreeIndex,
    allGroups: AssetGroup[],
    normalizedSearchTerm: string
): FilteredAssetTree => {
    const matchingAssetsByGroupId = new Map<string, IAsset[]>();
    const matchingAssetIds = new Set<string>();

    for (const asset of assetTreeIndex.assets) {
        if (normalizedSearchTerm && !asset.normalizedSearchText.includes(normalizedSearchTerm)) {
            continue;
        }

        matchingAssetIds.add(asset.id);
        for (const groupId of asset.groupIds) {
            const groupAssets = matchingAssetsByGroupId.get(groupId);
            if (groupAssets) {
                groupAssets.push(asset);
            } else {
                matchingAssetsByGroupId.set(groupId, [asset]);
            }
        }
    }

    const groups = allGroups.flatMap(group => {
        const groupAssets = matchingAssetsByGroupId.get(group.id);
        return groupAssets ? [{ id: group.id, name: group.name, assetsCount: groupAssets.length }] : [];
    });

    return { groups, assetsByGroupId: matchingAssetsByGroupId, assetIds: matchingAssetIds };
};

const filterTreeByGroups = (
    assetTreeIndex: AssetTreeIndex,
    allGroups: AssetGroup[],
    normalizedSearchTerm: string
): FilteredAssetTree => {
    const matchingGroups = allGroups.filter(group => group.name.toLowerCase().includes(normalizedSearchTerm));

    if (!normalizedSearchTerm) {
        return {
            groups: matchingGroups.map(group => ({
                id: group.id,
                name: group.name,
                assetsCount: assetTreeIndex.assetsByGroupId.get(group.id)?.length ?? 0,
            })),
            assetsByGroupId: assetTreeIndex.assetsByGroupId,
            assetIds: assetTreeIndex.allAssetIds,
        };
    }

    const matchingAssetIds = new Set<string>();
    const groups = matchingGroups.map(group => {
        const groupAssets = assetTreeIndex.assetsByGroupId.get(group.id) ?? [];
        groupAssets.forEach(asset => matchingAssetIds.add(asset.id));
        return { id: group.id, name: group.name, assetsCount: groupAssets.length };
    });

    return { groups, assetsByGroupId: assetTreeIndex.assetsByGroupId, assetIds: matchingAssetIds };
};

export const filterAssetTree = (
    assetTreeIndex: AssetTreeIndex,
    allGroups: AssetGroup[],
    searchTerm: string,
    viewMode: 'assets' | 'groups'
): FilteredAssetTree => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return viewMode === 'assets'
        ? filterTreeByAssets(assetTreeIndex, allGroups, normalizedSearchTerm)
        : filterTreeByGroups(assetTreeIndex, allGroups, normalizedSearchTerm);
};
